import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Briefcase, Building2, CheckCircle2, Gavel, HardHat, HeartPulse, Home, Landmark, ShieldCheck, Sparkles, TrendingUp, Users, Wrench } from "lucide-react";
import { TeaLogo } from "@/components/TeaLogo";

const GOLD = "#d4a017";
export const up = (d = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.6, ease: [0.22, 1, 0.36, 1] } });
export const pop = (d = 0) => ({ initial: { opacity: 0, scale: 0.6 }, animate: { opacity: 1, scale: 1 }, transition: { delay: d, type: "spring", stiffness: 220, damping: 16 } });

export function CountUp({ to, suffix = "", prefix = "" }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min((t - t0) / 1600, 1); setV(Math.round(to * (1 - (1 - p) ** 3))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{prefix}{v.toLocaleString()}{suffix}</>;
}

export const Glow = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_0_40px_rgba(212,160,23,0.08)] ${className}`}>{children}</div>
);

export function Cover({ name, finale, kicker = "Partner welcome", finaleKicker = "You're in" }) {
  const named = name && name !== "there";
  return (
    <div className="h-full grid place-items-center text-center relative">
      {finale && Array.from({ length: 18 }, (_, n) => (
        <motion.span key={n} className="absolute h-2 w-2 rounded-full" style={{ background: n % 3 ? GOLD : "#5eead4", left: "50%", top: "45%" }}
          initial={{ x: 0, y: 0, opacity: 1 }} animate={{ x: Math.cos(n * 0.35) * (120 + (n % 4) * 30), y: Math.sin(n * 0.35) * (110 + (n % 3) * 30), opacity: 0 }}
          transition={{ duration: 2.2, delay: 0.3 + (n % 5) * 0.08, repeat: Infinity, repeatDelay: 1.2 }} />))}
      <div className="space-y-5">
        <motion.div {...pop(0.1)} className="relative mx-auto w-fit">
          <span className="absolute -inset-5 rounded-full border-2 border-dashed border-[#d4a017]/50 tea-spin-slow" />
          <span className="absolute -inset-10 rounded-full bg-[#d4a017]/10 blur-2xl" />
          <TeaLogo size={88} testid="pw-cover-logo" />
        </motion.div>
        <motion.div {...up(0.4)} className="text-[11px] uppercase tracking-[0.35em] text-[#d4a017]">{finale ? finaleKicker : kicker}</motion.div>
        <motion.div {...up(0.6)} className="text-3xl sm:text-4xl font-semibold leading-tight">{finale ? (named ? <>Welcome to the Alliance,<br /><span className="text-[#f0d27a]">{name}</span>.</> : <>Welcome to<br /><span className="text-[#f0d27a]">The Exchange Alliance</span>.</>) : named ? <>Hello, <span className="text-[#f0d27a]">{name}</span>.</> : <>Welcome to<br /><span className="text-[#f0d27a]">your reset</span>.</>}</motion.div>
      </div>
    </div>
  );
}

export const Mission = () => (
  <div className="h-full flex flex-col justify-center gap-3 px-2">
    {["The Last", "The Least", "The Lost"].map((w, n) => (
      <motion.div key={w} {...up(0.25 + n * 0.45)} className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: n === 1 ? GOLD : "white" }}>{w}.</motion.div>))}
    <motion.p {...up(1.7)} className="text-sm text-white/60 pt-3 border-t border-white/10">Not a hand-out. Real, sustainable infrastructure.</motion.p>
  </div>
);

const STAGES = [["Acute crisis", ShieldCheck], ["Stabilized", HeartPulse], ["Employed", Briefcase], ["Skilled professional", BadgeCheck]];
const Journey = () => (
  <div className="h-full flex flex-col justify-center px-2">
    <div className="relative pl-8 space-y-6">
      <motion.span className="absolute left-[13px] top-2 w-0.5 bg-gradient-to-b from-[#d4a017] to-teal-300 origin-top" style={{ bottom: 8 }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 2.4, ease: "easeInOut" }} />
      {STAGES.map(([l, Icon], n) => (
        <motion.div key={l} {...up(0.3 + n * 0.55)} className="relative flex items-center gap-3">
          <span className="absolute -left-8 grid place-items-center h-7 w-7 rounded-full bg-[#0b0814] border-2" style={{ borderColor: n === 3 ? "#5eead4" : GOLD }}><Icon className="h-3.5 w-3.5" style={{ color: n === 3 ? "#5eead4" : GOLD }} /></span>
          <Glow className="flex-1 px-4 py-3"><div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Stage {n + 1}</div><div className="font-semibold">{l}</div></Glow>
        </motion.div>))}
    </div>
  </div>
);

const PILLARS = [["Supportive housing", Home], ["Peer accountability", Users], ["Clinical oversight", HeartPulse], ["Trade mastery", Wrench]];
const C2C = () => (
  <div className="h-full flex flex-col justify-center gap-4">
    <motion.div {...pop(0.1)} className="text-center"><div className="text-6xl font-black tracking-tight bg-gradient-to-r from-[#f0d27a] via-[#d4a017] to-[#f0d27a] bg-clip-text text-transparent">C2C</div><div className="text-[11px] uppercase tracking-[0.3em] text-white/50">Commitment to Career</div></motion.div>
    <div className="grid grid-cols-2 gap-2.5">{PILLARS.map(([l, Icon], n) => (
      <motion.div key={l} {...pop(0.5 + n * 0.25)}><Glow className="p-3 h-full"><Icon className="h-5 w-5 text-[#d4a017]" /><div className="mt-2 text-sm font-medium">{l}</div></Glow></motion.div>))}</div>
  </div>
);

const ROLE = {
  community: [HeartPulse, "Community & non-profit", ["Clinical stabilization", "Recovery foundations", "Community care"]],
  judicial: [Gavel, "Government & judicial", ["Legal pathways", "Public trust", "NCN accountability system"]],
  business: [HardHat, "Trade & business", ["Fill workforce shortages", "Certified, disciplined talent", "Motivated professionals"]],
};
function Role({ scene }) {
  const [Icon, label, items] = ROLE[scene];
  return (
    <div className="h-full flex flex-col justify-center gap-5">
      <motion.div {...pop(0.1)} className="relative mx-auto grid place-items-center h-28 w-28 rounded-3xl bg-gradient-to-br from-[#d4a017] to-[#7a5a08] shadow-[0_0_60px_rgba(212,160,23,0.45)]"><Icon className="h-14 w-14 text-black" /></motion.div>
      <motion.div {...up(0.4)} className="text-center text-[11px] uppercase tracking-[0.3em] text-[#d4a017]">{label}</motion.div>
      <div className="space-y-2">{items.map((t, n) => (
        <motion.div key={t} {...up(0.7 + n * 0.3)}><Glow className="flex items-center gap-2.5 px-4 py-2.5 text-sm"><CheckCircle2 className="h-4 w-4 text-teal-300 shrink-0" />{t}</Glow></motion.div>))}</div>
    </div>
  );
}

const NODES = [["Clinical", HeartPulse], ["Judicial", Landmark], ["Business", Building2], ["Referrals", Users], ["Housing", Home], ["Government", Gavel]];
const Alliance = () => (
  <div className="h-full grid place-items-center">
    <div className="relative h-64 w-64 sm:h-72 sm:w-72">
      <span className="absolute inset-6 rounded-full border border-white/10" /><span className="absolute inset-0 rounded-full border border-dashed border-[#d4a017]/30 tea-spin-slow" />
      <motion.div {...pop(0.1)} className="absolute inset-0 m-auto h-20 w-20 grid place-items-center rounded-full bg-[#0b0814] border-2 border-[#d4a017] shadow-[0_0_40px_rgba(212,160,23,0.5)]"><TeaLogo size={44} testid="pw-alliance-logo" /></motion.div>
      {NODES.map(([l, Icon], n) => {
        const a = (n / NODES.length) * Math.PI * 2 - Math.PI / 2;
        return (
          <motion.div key={l} {...pop(0.5 + n * 0.18)} className="absolute flex flex-col items-center gap-1" style={{ left: `calc(50% + ${Math.cos(a) * 42}% - 28px)`, top: `calc(50% + ${Math.sin(a) * 42}% - 28px)`, width: 56 }}>
            <span className="grid place-items-center h-11 w-11 rounded-full bg-white/[0.06] border border-white/15 backdrop-blur"><Icon className="h-4 w-4 text-[#f0d27a]" /></span>
            <span className="text-[9px] uppercase tracking-[0.15em] text-white/60">{l}</span>
          </motion.div>);
      })}
    </div>
  </div>
);

const KPIS = [["Program retention", 87, "%"], ["Employment placement", 74, "%"], ["12-month stability", 81, "%"]];
const Kpi = () => (
  <div className="h-full flex flex-col justify-center gap-3">
    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live partner dashboard · sample numbers</div>
    {KPIS.map(([l, v, s], n) => (
      <motion.div key={l} {...up(0.2 + n * 0.35)}><Glow className="p-4">
        <div className="flex items-end justify-between"><span className="text-sm text-white/70">{l}</span><span className="text-3xl font-bold text-[#f0d27a] tabular-nums"><CountUp to={v} suffix={s} /></span></div>
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><motion.div className="h-full bg-gradient-to-r from-[#d4a017] to-teal-300" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ delay: 0.3 + n * 0.35, duration: 1.6 }} /></div>
      </Glow></motion.div>))}
    <motion.div {...up(1.4)} className="flex items-center gap-1.5 text-xs text-emerald-300"><TrendingUp className="h-4 w-4" /> Trending up this quarter</motion.div>
  </div>
);

const MILES = ["Arrived & housed", "Baseline complete", "Hired · full time", "First $500 saved", "Trade certification"];
const Residents = () => (
  <div className="h-full flex flex-col justify-center">
    <Glow className="p-4 space-y-3">
      <div className="flex items-center gap-3"><span className="grid place-items-center h-10 w-10 rounded-full bg-[#d4a017]/20 text-[#f0d27a] font-semibold">JD</span><div><div className="font-semibold">Your referral</div><div className="text-[11px] text-white/50">Day 64 · Phase 2 · sample</div></div>
        <span className="ml-auto text-right"><span className="block text-xl font-bold text-teal-300 tabular-nums"><CountUp to={1240} prefix="$" /></span><span className="text-[10px] uppercase tracking-[0.2em] text-white/40">saved</span></span></div>
      <div className="space-y-1.5 pt-2 border-t border-white/10">{MILES.map((m, n) => (
        <motion.div key={m} {...up(0.4 + n * 0.4)} className="flex items-center gap-2 text-sm">
          {n < 4 ? <CheckCircle2 className="h-4 w-4 text-teal-300" /> : <Sparkles className="h-4 w-4 text-[#d4a017] animate-pulse" />}<span className={n < 4 ? "text-white/85" : "text-[#f0d27a]"}>{m}</span>{n === 4 && <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#d4a017]">next</span>}
        </motion.div>))}</div>
    </Glow>
  </div>
);

const PIECES = [["Clinical care", HeartPulse, -1, -1], ["Judicial pathways", Gavel, 1, -1], ["Business opportunity", Briefcase, -1, 1], ["Supportive housing", Home, 1, 1]];
const Unity = () => (
  <div className="h-full grid place-items-center">
    <div className="grid grid-cols-2 gap-2.5 w-full max-w-xs">{PIECES.map(([l, Icon, x, y], n) => (
      <motion.div key={l} initial={{ opacity: 0, x: x * 80, y: y * 80 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay: 0.2 + n * 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
        <Glow className="p-4 aspect-square flex flex-col justify-between"><Icon className="h-6 w-6 text-[#d4a017]" /><span className="text-sm font-medium leading-tight">{l}</span></Glow>
      </motion.div>))}</div>
    <motion.div {...pop(1.6)} className="mt-4 rounded-full bg-[#d4a017] text-black px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]">One framework</motion.div>
  </div>
);

const MAP = { mission: Mission, journey: Journey, c2c: C2C, alliance: Alliance, kpi: Kpi, residents: Residents, unity: Unity };

/** Animated sample visual for one partner-welcome step (re-mounts per step for the entrance). */
export function PartnerScene({ step, name }) {
  const Comp = MAP[step.scene];
  return (
    <div data-testid={`pw-scene-${step.scene}`} className="relative h-full overflow-hidden rounded-3xl border border-white/10 bg-[#0b0814]/80 p-5 sm:p-7 shadow-2xl">
      <span className="absolute right-4 top-3 z-20 text-[9px] uppercase tracking-[0.25em] text-white/30">The Exchange Alliance</span>
      {step.scene === "cover" || step.scene === "finale" ? <Cover name={name} finale={step.scene === "finale"} /> : ROLE[step.scene] ? <Role scene={step.scene} /> : <Comp />}
    </div>
  );
}
